import { useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { login } from "@/server/auth/login";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMutation } from "@tanstack/react-query";
import { cn, getNextRedirectRoute } from "@/lib/utils";
import { Alert, AlertDescription } from "./ui/alert";
import { useAppForm } from "./form/form-context";
import { AlertCircle } from "lucide-react";
import pkg from "../../package.json";
import logo from "@/assets/logo.png";
import { useAppSession } from "@/utils/session";

const packageVersion = pkg.version + "-" + (pkg as any).config?.release;

type Props = {
  redirect?: string;
};

export function Login({ className, redirect, ...props }: React.HTMLAttributes<HTMLDivElement> & Props) {
  const router = useRouter();
  const [loginError, setLoginError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: async (result) => {
      await router.invalidate();
      if (result.passwordExpiredFlow) {
        router.navigate({ to: "/password-expired" });
      } else if (redirect) {
        router.navigate({ to: redirect });
      } else {
        // Use the returned user data to determine next route based on permissions
        if (result.profile !== undefined && result.accessControl) {
          const user = {
            profile: result.profile,
            accessControl: result.accessControl,
          } as any;
          const nextRoute = getNextRedirectRoute(user);
          router.navigate({ to: nextRoute });
        } else {
          // Fallback - this shouldn't happen if login succeeded
          router.navigate({ to: "/gateways" });
        }
      }
    },
    onError: (error) => {
      setLoginError(error.message || "An error occurred while logging in");
    },
  });

  const form = useAppForm({
    defaultValues: {
      username: "",
      password: "",
    },
    onSubmit: (data) => {
      loginMutation.mutate({
        data: {
          username: data.value.username,
          password: data.value.password,
        },
      });
    },
  });

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <div className="p-8 flex flex-col justify-center">
            <div className="flex justify-center md:hidden">
              <img src={logo} alt="Logo" className="h-30 object-contain dark:invert" />
            </div>
            <CardHeader>
              <CardTitle className="text-2xl">Login</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                method="POST"
                className="p-6 md:p-8"
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
              >
                <div className="flex flex-col gap-6">
                  {loginError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{loginError}</AlertDescription>
                    </Alert>
                  )}
                  <form.AppField name="username">
                    {(field) => (
                      <field.FormTextField
                        label="Username"
                        id="username"
                        type="text"
                        error={field.state.meta.errors?.[0]}
                        autoComplete="username"
                        required
                      />
                    )}
                  </form.AppField>
                  <form.AppField name="password">
                    {(field) => (
                      <field.FormTextField
                        label="Password"
                        id="password"
                        type="password"
                        error={field.state.meta.errors?.[0]}
                        autoComplete="current-password"
                        aria-invalid={loginMutation.data ? "true" : "false"}
                        required
                      />
                    )}
                  </form.AppField>
                  <Button type="submit" className="w-full mt-8" disabled={loginMutation.status === "pending"}>
                    {loginMutation.status === "pending" ? "Logging in..." : "Login"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </div>

          <div className="relative hidden bg-muted md:block">
            <img src={logo} alt="Logo" className="absolute inset-0 h-full w-full object-cover dark:invert" />
          </div>
        </CardContent>
      </Card>

      <div className="text-center text-xs space-y-2 text-muted-foreground">
        <div>Client Version: {packageVersion}</div>
        <div>© nWise {currentYear}</div>
      </div>
    </div>
  );
}
